import { readonly, ref, type Ref } from 'vue';

/**
 * `beforeinstallprompt` is not part of the standard DOM typings, so it is declared here.
 */
export interface BeforeInstallPromptEvent extends Event {
    readonly userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>;
    prompt(): Promise<void>;
}

const BEFORE_INSTALL_PROMPT_EVENT = 'beforeinstallprompt';
const APP_INSTALLED_EVENT = 'appinstalled';

const deferredInstallPrompt = ref<BeforeInstallPromptEvent | null>(null);
const appInstalled = ref(false);

let isCaptureInitialized = false;

/**
 * The browser fires `beforeinstallprompt` very early, typically before the Vue app is mounted
 * (bootstrapping awaits the translation requests first). Listening for the event inside a component
 * would therefore miss it, and the deferred prompt would be lost for the whole session.
 *
 * This has to be called as early as possible during startup, before any async work happens.
 */
export function initPwaInstallPromptCapture(): void {
    if (isCaptureInitialized) {
        return;
    }
    isCaptureInitialized = true;

    window.addEventListener(BEFORE_INSTALL_PROMPT_EVENT, (event) => {
        // Prevent the browser's own mini-infobar so that the install can be triggered by our dialog instead.
        event.preventDefault();
        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- see BeforeInstallPromptEvent
        deferredInstallPrompt.value = event as BeforeInstallPromptEvent;
    });

    window.addEventListener(APP_INSTALLED_EVENT, () => {
        appInstalled.value = true;
        deferredInstallPrompt.value = null;
    });
}

export function getDeferredInstallPrompt(): Ref<BeforeInstallPromptEvent | null> {
    return deferredInstallPrompt;
}

export function clearDeferredInstallPrompt(): void {
    deferredInstallPrompt.value = null;
}

export function markAppAsInstalled(): void {
    appInstalled.value = true;
    deferredInstallPrompt.value = null;
}

/**
 * Resets the module level state. Only intended for tests, since the state lives for the whole
 * lifetime of the application at runtime.
 */
export function resetPwaInstallState(): void {
    deferredInstallPrompt.value = null;
    appInstalled.value = false;
}

export const isAppInstalled = readonly(appInstalled);
